import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import { Subcontent, AntdAlert as Alert } from '@yuhuinc/ui-library/';

import { LeaseCreatorPageFooter } from '../common/LeaseCreatorPageFooter';
import { StyledContainer, StyledRow } from '../common/LeaseStyles';
import LeaseHeaderProgress from '../common/LeaseHeaderProgress';
import { LeaseRejectedAlert } from '../common/LeaseRejectedAlert';

import ResidentDetails from './ResidentDetails';
import LeaseDetails from './LeaseDetails';
import Pricing from './Pricing';
import RentalIncentives from './RentalIncentives';
import ServicesUtilities from './ServicesUtilities';
import BaseLease from './BaseLease';
import LeaseAddenda from './LeaseAddenda';
import AdditionalInformation from './AdditionalInformation';

import { getTranslatedString } from 'helpers/i18n';
import { leaseCreatorColors } from 'helpers/ColorHelper';

import { checkUnitAvailabilityForLease } from 'actions/landlordPropertiesActions';
import { media, sizes } from 'helpers/MediaHelper';
import { responsive } from 'helpers/StyleHelper';

const StyledSubcontentContainer = styled.div`
  max-width: 560px;
  margin: 0 0 15px 15px;
  ${media.mobile`
    margin: 0 16px 16px 16px;
  `};
`;

const StyledPageHeader = styled.div`
  font-size: 24px;
  line-height: 32px;
  color: 38;
  font-weight: 400;
`;

const StyledSummaryContainer = styled.div`
  background-color: white;
  width: 100%;
  margin-top: 24px;
  border: 1px solid ${leaseCreatorColors.FORM_BORDER};
  border-radius: 5px;
`;

const StyledSummary = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-size: 24px;
  padding: 16px;
  max-width: 580px;
`;

const LeaseSummaryPage = ({
  forward,
  back,
  goTo,
  currentPage,
  totalPages,
  leaseFormValues,
  leaseDetails,
  handleCancellation,
  units,
  unitTypes,
  checkUnitAvailabilityForLease,
  checkingUnitAvailability,
  selectedBaseDocumentTemplate,
  latestLeaseReview,
}) => {
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedUnitType, setSelectedUnitType] = useState(null);
  const [unitAvailabilityError, setUnitAvailabilityError] = useState([]);

  const isMobile = window.matchMedia(`(max-width: ${sizes.mobile}px)`).matches;
  const labelSize = isMobile ? responsive.text.mobile : responsive.text.desktop;

  const handleEdit = (page) => {
    goTo(page);
  };

  useEffect(() => {
    if (leaseDetails.draft_lease_data && leaseDetails.draft_lease_data.unit_id) {
      const unit = units.find((unit) => unit.value === leaseDetails.draft_lease_data.unit_id);
      setSelectedUnit(unit);
    }
    if (leaseDetails.draft_lease_data && leaseDetails.draft_lease_data.unit_type_id) {
      const unitType = unitTypes.find(
        (unitType) => unitType.value === leaseDetails.draft_lease_data.unit_type_id
      );
      setSelectedUnitType(unitType);
    }
  }, [units, unitTypes, leaseDetails]);

  const checkUnitAvailabilityAndContinue = async () => {
    setUnitAvailabilityError([]);
    if (leaseFormValues.delay_unit_assignment) {
      forward();
      return;
    }
    const response = await checkUnitAvailabilityForLease({
      propertyId: leaseFormValues.property_id,
      unitId: leaseFormValues.unit_id,
      leaseStartDate: leaseFormValues.lease_start_date,
      leaseEndDate: leaseFormValues.lease_end_date,
    });
    if (response.action.payload.available) {
      forward();
    } else {
      setUnitAvailabilityError(response.action.payload);
    }
  };

  return (
    <StyledContainer>
      <LeaseHeaderProgress lease={leaseDetails} stepsFilled={currentPage} totalSteps={totalPages} />

      <StyledSummaryContainer>
        <StyledSummary>
          <StyledPageHeader>
            {getTranslatedString('landlordPortal.leaseCreatorV2.leaseSummaryPage.leaseSummary')}
          </StyledPageHeader>
        </StyledSummary>

        <StyledSubcontentContainer>
          {latestLeaseReview?.status === 'rejected' && (
            <StyledRow gutter={16}>
              <LeaseRejectedAlert latestLeaseReview={latestLeaseReview} />
            </StyledRow>
          )}
          {!isEmpty(unitAvailabilityError) && !unitAvailabilityError.available && (
            <StyledRow>
              <Alert
                type="error"
                message={unitAvailabilityError.occupying_leases.map((occupying_lease) => (
                  <div>{`Unit is occupied by ${occupying_lease.cotenants} from ${occupying_lease.start_date} to ${occupying_lease.end_date}`}</div>
                ))}
                closable={false}
                showIcon={true}
              />
            </StyledRow>
          )}
          <Subcontent
            label={getTranslatedString(
              'landlordPortal.leaseCreatorV2.leaseSummaryPage.residentDetails'
            )}
            labelSize={labelSize}
            hideDelete={true}
            hideEdit={true}
            className="subcontent-container"
          >
            <ResidentDetails leaseDetails={leaseDetails} />
          </Subcontent>
        </StyledSubcontentContainer>
        <StyledSubcontentContainer>
          <Subcontent
            label={getTranslatedString(
              'landlordPortal.leaseCreatorV2.leaseSummaryPage.leaseDetails'
            )}
            labelSize={labelSize}
            hideDelete={true}
            onEditClick={() => handleEdit(1)}
          >
            <LeaseDetails
              leaseDetails={leaseDetails}
              leaseFormValues={leaseFormValues}
              selectedUnit={selectedUnit}
              selectedUnitType={selectedUnitType}
            />
          </Subcontent>
        </StyledSubcontentContainer>
        <StyledSubcontentContainer>
          <Subcontent
            label={getTranslatedString('landlordPortal.leaseCreatorV2.leaseSummaryPage.pricing')}
            labelSize={labelSize}
            hideDelete={true}
            onEditClick={() => handleEdit(2)}
          >
            <Pricing leaseDetails={leaseDetails} />
          </Subcontent>
        </StyledSubcontentContainer>
        <StyledSubcontentContainer>
          <Subcontent
            label={getTranslatedString(
              'landlordPortal.leaseCreatorV2.leaseSummaryPage.rentalIncentives'
            )}
            labelSize={labelSize}
            hideDelete={true}
            onEditClick={() => handleEdit(3)}
          >
            <RentalIncentives leaseDetails={leaseDetails} />
          </Subcontent>
        </StyledSubcontentContainer>
        <StyledSubcontentContainer>
          <Subcontent
            label={getTranslatedString(
              'landlordPortal.leaseCreatorV2.leaseSummaryPage.servicesAndUtilities'
            )}
            labelSize={labelSize}
            hideDelete={true}
            onEditClick={() => handleEdit(4)}
          >
            <ServicesUtilities leaseDetails={leaseDetails} />
          </Subcontent>
        </StyledSubcontentContainer>
        <StyledSubcontentContainer>
          <Subcontent
            label={getTranslatedString('landlordPortal.leaseCreatorV2.leaseSummaryPage.baseLease')}
            labelSize={labelSize}
            hideDelete={true}
            onEditClick={() => handleEdit(5)}
          >
            <BaseLease selectedBaseDocumentTemplate={selectedBaseDocumentTemplate} />
          </Subcontent>
        </StyledSubcontentContainer>
        <StyledSubcontentContainer>
          <Subcontent
            label={getTranslatedString(
              'landlordPortal.leaseCreatorV2.leaseSummaryPage.leaseAddenda'
            )}
            labelSize={labelSize}
            hideDelete={true}
            onEditClick={() => handleEdit(5)}
          >
            <LeaseAddenda leaseDetails={leaseDetails} />
          </Subcontent>
        </StyledSubcontentContainer>
        <StyledSubcontentContainer>
          <Subcontent
            label={getTranslatedString(
              'landlordPortal.leaseCreatorV2.leaseSummaryPage.additionalInformation'
            )}
            labelSize={labelSize}
            hideDelete={true}
            onEditClick={() => handleEdit(6)}
          >
            <AdditionalInformation leaseDetails={leaseDetails} />
          </Subcontent>
        </StyledSubcontentContainer>
      </StyledSummaryContainer>

      <LeaseCreatorPageFooter
        forward={checkUnitAvailabilityAndContinue}
        back={back}
        currentPage={currentPage}
        totalPages={totalPages}
        handleCancellation={handleCancellation}
        submitLoading={checkingUnitAvailability}
      />
    </StyledContainer>
  );
};

const mapStoreToProps = (store) => ({
  units: store.landlordProperties.units,
  unitTypes: store.landlordProperties.unitTypes,
  checkingUnitAvailability: store.landlordProperties.checkingUnitAvailability,
  selectedBaseDocumentTemplate: store.landlord.leaseCreatorV2.selectedBaseDocumentTemplate,
  latestLeaseReview: store.landlord.leaseCreatorV2.latestLeaseReview,
});

const mapDispatchToProps = { checkUnitAvailabilityForLease };

export default connect(mapStoreToProps, mapDispatchToProps)(LeaseSummaryPage);
